import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Briefcase, UserRound, Building2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import GoogleButton from "../components/GoogleButton";
import Seo from "../components/Seo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: searchParams.get("role") === "employer" ? "employer" : "candidate", company_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [chQuery, setChQuery] = useState("");
  const [chResults, setChResults] = useState([]);
  const [chLoading, setChLoading] = useState(false);
  const [chSelected, setChSelected] = useState(null);
  const [chManual, setChManual] = useState(false);
  const [chError, setChError] = useState("");
  const [registered, setRegistered] = useState(null);
  const [website, setWebsite] = useState(""); // honeypot — genuine users never see or fill this
  const [formStartedAt] = useState(() => Date.now());
  const [resendState, setResendState] = useState("idle");
  const turnstileRef = useRef(null);
  const turnstileId = useRef(null);
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  // Cloudflare Turnstile widget — rendered only when a site key is configured
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    const render = () => {
      if (window.turnstile && turnstileRef.current && turnstileId.current === null) {
        turnstileId.current = window.turnstile.render(turnstileRef.current, { sitekey: TURNSTILE_SITE_KEY });
      }
    };
    if (window.turnstile) { render(); return undefined; }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
    return undefined;
  }, [TURNSTILE_SITE_KEY]);

  // live Companies House autocomplete, debounced so we never hammer the API
  useEffect(() => {
    if (form.role !== "employer" || chManual || chSelected) return undefined;
    const q = chQuery.trim();
    if (q.length < 2) { setChResults([]); return undefined; }
    const t = setTimeout(async () => {
      setChLoading(true);
      setChError("");
      try {
        const r = await api.get("/companies-house/search", { params: { q } });
        setChResults(r.data.items || []);
      } catch (err) {
        setChResults([]);
        setChError(formatApiError(err, "Companies House search is unavailable right now"));
      } finally {
        setChLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [chQuery, form.role, chManual, chSelected]);

  const selectCompany = async (item) => {
    setChLoading(true);
    setChError("");
    try {
      const r = await api.get(`/companies-house/company/${item.company_number}`);
      setChSelected(r.data);
      setChResults([]);
      setForm((f) => ({ ...f, company_name: r.data.company_name }));
    } catch (err) {
      setChError(formatApiError(err, "Couldn't load that company's record"));
    } finally {
      setChLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const turnstile_token = TURNSTILE_SITE_KEY && window.turnstile && turnstileId.current !== null
        ? window.turnstile.getResponse(turnstileId.current) : "";
      const res = await register({ ...form, company_number: chSelected?.company_number || "", source: new URLSearchParams(window.location.search).get("source") || "direct", website, form_started_at: formStartedAt, turnstile_token });
      if (res?.verification_required) {
        setRegistered({ email: form.email, sent_to: res.sent_to || "" });
        return;
      }
      const next = searchParams.get("next");
      if (next) navigate(next);
      else navigate(res.role === "admin" ? "/control-tower" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err, "Could not create your account"));
      if (TURNSTILE_SITE_KEY && window.turnstile && turnstileId.current !== null) window.turnstile.reset(turnstileId.current);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/verify/email/resend", { email: registered?.email || "" });
    } catch {
      /* the response is intentionally identical either way */
    }
    setResendState("sent");
  };

  if (registered) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="register-verify-page">
        <Seo title="Check your email | HiredToday" description="Verify your email address to activate your HiredToday account." path="/register" />
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Check your email.</h1>
        <p className="mt-3 text-sm text-zinc-600" data-testid="register-verify-copy">
          We've sent a verification link{registered.sent_to ? ` to ${registered.sent_to}` : ""}. Click it within
          15 minutes to activate your account — it works once. Until then the account stays inactive.
        </p>
        <button type="button" onClick={resendVerification} disabled={resendState !== "idle"}
          data-testid="register-resend-verification"
          className="mt-6 h-12 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
          {resendState === "sent" ? "If the account needs it, a fresh link is on its way" : resendState === "sending" ? "Sending…" : "Resend verification link"}
        </button>
        <p className="mt-6 text-sm text-zinc-500">
          Already verified? <Link to="/login" data-testid="register-verify-login" className="text-blue-700 font-medium">Log in</Link>
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="register-page">
      <Seo title="Create an Account | HiredToday" description="Create a free HiredToday account as a jobseeker or an employer." path="/register" />
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Create your account.</h1>
      <p className="mt-2 text-sm text-zinc-500">Free, and it takes about a minute.</p>

      <div className="grid grid-cols-2 gap-3 mt-8" data-testid="role-selector">
        {[
          { id: "candidate", label: "I'm looking for work", Icon: UserRound },
          { id: "employer", label: "I'm hiring", Icon: Briefcase },
        ].map(({ id, label, Icon }) => (
          <button type="button" key={id} onClick={() => setForm({ ...form, role: id })} data-testid={`role-${id}`}
            className={`rounded-md border-2 p-4 text-left transition-colors duration-200 ${
              form.role === id ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}>
            <Icon className="h-5 w-5 text-zinc-700 mb-2" />
            <p className="text-sm font-semibold text-zinc-900">{label}</p>
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-6 border border-red-200 bg-red-50 text-red-800 text-sm rounded-md px-4 py-3" data-testid="register-error">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="mt-6 space-y-5" data-testid="register-form">
        {form.role === "candidate" && (
          <>
            <GoogleButton label="Sign up with Google" />
            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="flex-1 h-px bg-zinc-200" />
              <span className="text-xs text-zinc-400">or with email</span>
              <span className="flex-1 h-px bg-zinc-200" />
            </div>
          </>
        )}
        <div>
          <label className="text-sm font-medium text-zinc-700 block mb-1.5">
            {form.role === "employer" ? "Your name" : "Full name"}
          </label>
          <input required data-testid="register-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        {form.role === "employer" && (
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5" htmlFor="ch-search">Your company</label>
            {chSelected ? (
              <div className="border border-green-300 bg-green-50/50 rounded-md p-4" data-testid="ch-confirm-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-zinc-950 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /> {chSelected.company_name}
                    </p>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-600">
                      <dt className="font-medium text-zinc-500">Company No</dt><dd data-testid="ch-confirm-number">{chSelected.company_number}</dd>
                      <dt className="font-medium text-zinc-500">Status</dt><dd className="capitalize">{chSelected.company_status}</dd>
                      <dt className="font-medium text-zinc-500">Type</dt><dd>{chSelected.company_type || "—"}</dd>
                      <dt className="font-medium text-zinc-500">Incorporated</dt><dd>{chSelected.date_of_creation || "—"}</dd>
                      <dt className="font-medium text-zinc-500 col-span-2">Registered office</dt>
                      <dd className="col-span-2">{chSelected.registered_office || "—"}</dd>
                      {chSelected.sic_codes?.length > 0 && (
                        <><dt className="font-medium text-zinc-500 col-span-2">Industry (SIC)</dt>
                        <dd className="col-span-2">{chSelected.sic_codes.join(", ")}</dd></>
                      )}
                    </dl>
                    <p className="text-[11px] text-zinc-500 mt-2">Official record from Companies House. These details are locked once your account is created.</p>
                  </div>
                  <button type="button" onClick={() => { setChSelected(null); setChQuery(""); setForm((f) => ({ ...f, company_name: "" })); }}
                    data-testid="ch-change-company" className="shrink-0 text-xs font-semibold text-blue-700 hover:text-blue-900">
                    Change
                  </button>
                </div>
              </div>
            ) : chManual ? (
              <div>
                <input data-testid="register-company" value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Your trading name or organisation"
                  className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <p className="text-xs text-zinc-500 mt-1.5" data-testid="ch-manual-note">
                  No problem, sole traders, charities and overseas organisations are welcome. We'll review your details manually before your jobs go live.
                </p>
                <button type="button" onClick={() => { setChManual(false); setForm((f) => ({ ...f, company_name: "" })); }}
                  data-testid="ch-back-to-search" className="text-xs font-semibold text-blue-700 hover:text-blue-900 mt-1">
                  Search Companies House instead
                </button>
              </div>
            ) : (
              <div className="relative">
                <input id="ch-search" data-testid="ch-search" value={chQuery} autoComplete="off"
                  onChange={(e) => setChQuery(e.target.value)}
                  placeholder="Start typing your company's official name…"
                  aria-describedby="ch-search-hint"
                  className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <p id="ch-search-hint" className="text-xs text-zinc-400 mt-1.5">
                  We search live Companies House records, active UK companies appear as you type.
                </p>
                {chLoading && <div className="absolute right-4 top-4 h-4 w-4 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />}
                {chResults.length > 0 && (
                  <ul className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
                    role="listbox" data-testid="ch-results">
                    {chResults.map((item) => (
                      <li key={item.company_number}>
                        <button type="button" onClick={() => selectCompany(item)} role="option" aria-selected="false"
                          data-testid={`ch-option-${item.company_number}`}
                          className="w-full text-left px-4 py-2.5 hover:bg-zinc-50 focus:bg-zinc-50 focus:outline-none transition-colors duration-150">
                          <span className="text-sm font-semibold text-zinc-900">{item.company_name}</span>
                          <span className="ml-2 text-xs text-zinc-500">{item.company_number} · {item.company_status}</span>
                          {item.address_snippet && <span className="block text-xs text-zinc-400 truncate">{item.address_snippet}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {chError && <p className="text-xs text-amber-700 mt-1.5" role="alert" data-testid="ch-search-error">{chError}, you can use manual entry below.</p>}
                {chQuery.trim().length >= 2 && !chLoading && chResults.length === 0 && !chError && (
                  <p className="text-xs text-zinc-500 mt-1.5" data-testid="ch-no-results">No active company matches that name.</p>
                )}
                <button type="button" onClick={() => setChManual(true)} data-testid="ch-not-listed"
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900 mt-1.5">
                  My company isn't listed (sole trader, charity, overseas…)
                </button>
              </div>
            )}
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-zinc-700 block mb-1.5">Email</label>
          <input required type="email" data-testid="register-email" value={form.email}
            autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 block mb-1.5">Password</label>
          <input required type="password" minLength={8} data-testid="register-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <p className="text-xs text-zinc-400 mt-1.5">At least 8 characters.</p>
        </div>
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
          <label htmlFor="reg-website">Website</label>
          <input id="reg-website" type="text" name="website" tabIndex={-1} autoComplete="off"
            value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="register-website-hp" />
        </div>
        {TURNSTILE_SITE_KEY && <div ref={turnstileRef} data-testid="register-turnstile" className="cf-turnstile" />}
        <button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
          {loading ? "Creating account…" : form.role === "employer" ? "Create employer account" : "Create free account"}
        </button>
      </form>
      <p className="mt-6 text-sm text-zinc-500 text-center">
        Already have an account?{" "}
        <Link to="/login" data-testid="register-to-login" className="text-blue-700 font-medium">Log in</Link>
      </p>
    </main>
  );
}
